import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { Button, SpecraButton, SpecraIconButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Typography from '@tiptap/extension-typography';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { ArrowUp, Brain, Library, Paperclip, Share2, Square, UploadCloud, Zap } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Markdown } from 'tiptap-markdown';
import { useAuth } from '../contexts/authContext';
import { useAttachmentUploads } from '../hooks/useAttachmentUploads';
import { apiErrorMessage } from '../lib/api/apiErrorMessage';
import { applyQuickPrefix, readQuickMode, writeQuickMode } from '../lib/chat/depth';
import { mentionHref, mentionQuery, useMentionSearch } from '../lib/chat/mentions';
import { artifactClient, sharepointClient } from '../lib/connect/clients';
import { createPendingRow } from '../lib/upload';
import { describeComposerFileCaps, MAX_FILES_PER_MESSAGE, partitionComposerFiles } from '../lib/upload/limits';
import { cn } from '../lib/utils';
import { COMPACT_ATTACHMENT_THRESHOLD, CompactAttachmentList } from './CompactAttachmentList';
import { DocumentLibraryPicker } from './DocumentLibraryPicker';
import { FileAttachment } from './FileAttachment';
import { MentionMenu } from './MentionMenu';
import { SharePointDocumentPicker } from './SharePointDocumentPicker';
import { VoiceInputButton } from './VoiceInputButton';
/** TipTap Typography options for the chat composer (SAD-883). */ export const composerTypographyOptions = {
    // Smart-quote input rules rewrite " / ' mid-keystroke and can throw
    // RangeError: Position N out of range in this composer.
    openDoubleQuote: false,
    closeDoubleQuote: false,
    openSingleQuote: false,
    closeSingleQuote: false
};
export function ComposerInput({ onSubmit, onStop, submitLabel = 'Ask Specra.AI', deferClear = false, submitting, resetKey, mode = 'default', dense = false, placeholder: placeholderProp, draft, onRemember, rememberDisabled = false, attachmentChatId, quickToggle = false }) {
    const { user } = useAuth();
    const placeholder = placeholderProp ?? (mode === 'chat' ? 'Ask anything. Type @ to point at a program, document or person.' : 'Ask Specra.AI anything...');
    const [quick, setQuick] = useState(()=>quickToggle && readQuickMode());
    const toggleQuick = ()=>{
        setQuick(!quick);
        writeQuickMode(!quick);
    };
    const [isSharePointPickerOpen, setIsSharePointPickerOpen] = useState(false);
    const [isDocumentLibraryOpen, setIsDocumentLibraryOpen] = useState(false);
    const sharePointDownloads = useRef(new Map());
    const uploads = useAttachmentUploads({
        persistKey: `pendingAttachments::${user?.tenant_id ?? 'signed-out'}::${user?.id ?? 'signed-out'}::${window.location.pathname}`
    });
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef(null);
    const editorRootRef = useRef(null);
    const [prompt, setPrompt] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isProcessingDocs, setIsProcessingDocs] = useState(false);
    const [isComposing, setIsComposing] = useState(false);
    const isSubmittingRef = useRef(false); // Track busy state immediately
    const isBusy = (submitting ?? isSubmitting) === true || isSubmittingRef.current;
    // @-mentions: the text after an @ before the caret, the matching records,
    // and which one the arrow keys are on. The editor's key handler is created
    // once, so it reads them through a ref.
    const [mention, setMention] = useState(null);
    const [mentionIndex, setMentionIndex] = useState(0);
    // Mentions are a chat composer feature: elsewhere an "@" is just text, and
    // Enter must submit rather than pick a hidden result.
    const mentionResults = useMentionSearch(mode === 'chat' ? mention : null);
    const mentionRef = useRef({
        open: false,
        hits: [],
        index: 0,
        pick: ()=>{},
        close: ()=>{},
        move: ()=>{}
    });
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: {
                    levels: [
                        1,
                        2,
                        3,
                        4
                    ]
                }
            }),
            Typography.configure(composerTypographyOptions),
            Link.configure({
                autolink: true,
                openOnClick: false,
                defaultProtocol: 'https',
                // @-mentions are links to the picked record (lib/chat/mentions).
                protocols: [
                    'mention'
                ]
            }),
            Placeholder.configure({
                placeholder
            }),
            Markdown.configure({
                html: false
            })
        ],
        content: '',
        immediatelyRender: false,
        autofocus: false,
        editable: !isBusy,
        editorProps: {
            attributes: {
                class: cn('prose prose-sm max-w-none focus:outline-none outline-none bg-transparent overflow-y-auto prose-p:my-1', dense ? 'min-h-[2.25rem] max-h-40 text-[12.5px]' : 'min-h-[3.5rem] max-h-56 text-[13.5px]'),
                'data-testid': Testids.assistant.chatInput
            },
            handleKeyDown: (_view, event)=>{
                const m = mentionRef.current;
                if (m.open) {
                    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                        event.preventDefault();
                        m.move(event.key === 'ArrowDown' ? 1 : -1);
                        return true;
                    }
                    if ((event.key === 'Enter' || event.key === 'Tab') && m.hits.length > 0) {
                        event.preventDefault();
                        m.pick(m.hits[Math.min(m.index, m.hits.length - 1)]);
                        return true;
                    }
                    if (event.key === 'Escape') {
                        event.preventDefault();
                        m.close();
                        return true;
                    }
                }
                // Shift+Enter → new line, but if previous block is styled (heading/list/code),
                // create a fresh paragraph block; otherwise insert a soft line break.
                if (event.key === 'Enter' && event.shiftKey) {
                    event.preventDefault();
                    const isStyledBlock = editor?.isActive('heading') || editor?.isActive('bulletList') || editor?.isActive('orderedList') || editor?.isActive('codeBlock') || editor?.isActive('blockquote');
                    if (isStyledBlock) {
                        editor?.chain().focus().splitBlock().setParagraph().run();
                    } else {
                        editor?.commands.setHardBreak();
                    }
                    return true;
                }
                // Enter → send (when not composing or busy)
                if (event.key === 'Enter' && !event.shiftKey && !isComposing && !isBusy) {
                    const text = editor?.getText() ?? '';
                    if (text.trim().length === 0 && uploads.rows.length === 0) return false;
                    event.preventDefault();
                    void handleSubmit();
                    return true;
                }
                return false;
            }
        },
        onUpdate: ({ editor })=>{
            // Keep a lightweight text mirror to enable/disable submit states
            setPrompt(editor.getText());
            readMention(editor);
        },
        onSelectionUpdate: ({ editor })=>readMention(editor)
    });
    // A starter or example picked outside the composer replaces the draft; the
    // update runs the mention check, so an example ending in "@" opens the menu.
    useEffect(()=>{
        if (!editor || !draft) return;
        editor.commands.setContent(draft.text, {
            emitUpdate: true
        });
        editor.commands.focus('end');
    }, [
        editor,
        draft
    ]);
    function readMention(ed) {
        const { $from, empty } = ed.state.selection;
        const before = empty ? $from.parent.textBetween(0, $from.parentOffset, undefined, '\ufffc') : '';
        const q = mentionQuery(before);
        setMention(q);
        if (q === null) setMentionIndex(0);
    }
    // Replace "@query" before the caret with a link to the picked record.
    const pickMention = (hit)=>{
        if (!editor || mention === null) return;
        const to = editor.state.selection.from;
        editor.chain().focus().deleteRange({
            from: to - mention.length - 1,
            to
        }).insertContent([
            {
                type: 'text',
                text: `@${hit.label}`,
                marks: [
                    {
                        type: 'link',
                        attrs: {
                            href: mentionHref(hit.kind, hit.id)
                        }
                    }
                ]
            },
            {
                type: 'text',
                text: ' '
            }
        ]).unsetMark('link').run();
        setMention(null);
        setMentionIndex(0);
    };
    mentionRef.current = {
        open: mode === 'chat' && mention !== null,
        hits: mentionResults.hits,
        index: mentionIndex,
        pick: pickMention,
        close: ()=>setMention(null),
        move: (d)=>setMentionIndex((i)=>{
                const n = mentionResults.hits.length;
                return n === 0 ? 0 : (i + d + n) % n;
            })
    };
    // Dictation lands at the end of whatever is already drafted, as plain
    // text: inserted as content it would be parsed as markdown, and a spoken
    // "star" or "hash" is not formatting.
    const insertDictation = (text)=>{
        if (!editor) return;
        const spacer = editor.isEmpty ? '' : ' ';
        editor.chain().focus('end').insertContent({
            type: 'text',
            text: spacer + text
        }).run();
    };
    const handleFileChange = (event)=>{
        if (event.target.files) {
            const { accepted, oversized, overCount } = partitionComposerFiles(Array.from(event.target.files), uploads.rows.length);
            const capMessage = describeComposerFileCaps(oversized, overCount);
            if (capMessage) {
                alert(capMessage);
            }
            uploads.addFiles(accepted, user?.tenant_id ? {
                tenantId: user.tenant_id,
                ownerUserId: user.id
            } : undefined);
            event.target.value = '';
        }
    };
    const handleDragEnter = (e)=>{
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
            setIsDragging(true);
        }
    };
    const handleDragLeave = (e)=>{
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };
    const handleDragOver = (e)=>{
        e.preventDefault();
        e.stopPropagation();
    };
    const handleDrop = (e)=>{
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const { accepted, oversized, overCount } = partitionComposerFiles(Array.from(e.dataTransfer.files), uploads.rows.length);
            const capMessage = describeComposerFileCaps(oversized, overCount);
            if (capMessage) {
                alert(capMessage);
            }
            uploads.addFiles(accepted, user?.tenant_id ? {
                tenantId: user.tenant_id,
                ownerUserId: user.id
            } : undefined);
            e.dataTransfer.clearData();
        }
    };
    // Recover from a failed attachment row, addressed by its stable id — never
    // by array index, which shifts under a concurrent remove. Two failure
    // classes, two strategies (picked by `uploads.retry`):
    //   1. Upload failed before any processing job existed — re-drive
    //      uploadAttachmentViaTesoro against the cached File blob.
    //   2. Processing failed after a successful upload — call
    //      ArtifactService.RetryProcessing so reverie re-runs the same bytes.
    const handleRetry = async (id)=>{
        const source = sharePointDownloads.current.get(id);
        if (source) {
            await handleSharePointDocumentSelect(source, id);
            return;
        }
        if (!user || !user.tenant_id) {
            toast.error('You must be logged in (with a tenant) to retry an upload');
            return;
        }
        await uploads.retry(id, {
            tenantId: user.tenant_id,
            ownerUserId: user.id
        });
    };
    const openFileDialog = ()=>{
        fileInputRef.current?.click();
    };
    // Every source shares the per-message file cap, not only the picker and drop.
    const atFileCap = ()=>{
        if (uploads.rows.length < MAX_FILES_PER_MESSAGE) return false;
        toast.error(describeComposerFileCaps([], 1));
        return true;
    };
    const handleLibraryDocumentSelect = async (document)=>{
        if (uploads.rows.some((row)=>row.attachmentId === document.artifactId)) return;
        if (atFileCap()) return;
        let jobId;
        try {
            await artifactClient.getArtifactChunk({
                artifactId: document.artifactId,
                chunkKind: 'processed_markdown'
            });
        } catch (error) {
            if (!(error instanceof ConnectError) || error.code !== Code.NotFound) throw error;
            const started = await artifactClient.startArtifactProcessing({
                artifactId: document.artifactId
            });
            jobId = started.job?.jobId;
            if (!jobId) throw new Error('Document processing could not start. Try again.');
        }
        const file = new File([], document.filename, {
            type: document.mimeType
        });
        Object.defineProperty(file, 'size', {
            value: Number(document.sizeBytes)
        });
        if (uploads.rows.some((row)=>row.attachmentId === document.artifactId)) return;
        uploads.addRow(createPendingRow(file, {
            progress: 100,
            status: 'uploaded',
            attachmentId: document.artifactId,
            contentType: document.mimeType,
            jobId,
            processingStatus: jobId ? 'queued' : 'ready'
        }));
    };
    const handleSharePointDocumentSelect = async (document, existingRowId)=>{
        if (!user) {
            toast.error('You must be logged in to select SharePoint documents');
            return;
        }
        if (!existingRowId && atFileCap()) return;
        // Add placeholder file immediately so the user sees download progress.
        // Addressed by its own stable id from here on \u2014 never by array index.
        const placeholderFile = new File([], document.title, {
            type: 'application/octet-stream'
        });
        const row = createPendingRow(placeholderFile, {
            status: 'uploading',
            statusLabel: 'Downloading from SharePoint\u2026',
            sourceDownloadPending: true,
            contentType: 'application/octet-stream'
        });
        const rowId = existingRowId ?? row.id;
        sharePointDownloads.current.set(rowId, document);
        if (existingRowId) {
            uploads.updateRow(rowId, (r)=>({
                    ...r,
                    status: 'uploading',
                    progress: 0,
                    statusLabel: row.statusLabel
                }));
        } else {
            uploads.addRow(row);
        }
        try {
            // Parse the resource id. Only driveItem is reachable: every resource_id
            // enies-lobby emits from listDriveItems and searchDocuments is a
            // driveItem, and GetDriveItemContent addresses a drive. listItem is
            // rejected loudly rather than downloaded as the wrong thing — supporting
            // it needs a list_id on the RPC and a Graph path that does not exist yet.
            const [resourceType, ...pathParts] = document.resource_id.split(':');
            if (resourceType !== 'driveItem') {
                throw new Error(`Unsupported SharePoint resource type: ${resourceType}`);
            }
            const [siteId, driveId] = [
                pathParts[0]?.split('/')[0],
                pathParts[0]?.split('/')[1]
            ];
            const res = await sharepointClient.getDriveItemContent({
                siteId,
                driveId,
                itemId: document.id
            });
            let sharePointFile;
            if (res.content.length > 0) {
                // Inline bytes: the whole message arrives at once, so there is no
                // progress to report between 0 and done. The streaming read this
                // replaced only ever ran on the binary branch.
                uploads.updateRow(rowId, (r)=>({
                        ...r,
                        progress: 100
                    }));
                sharePointFile = new File([
                    res.content
                ], res.fileName || document.title, {
                    type: res.mimeType || 'application/octet-stream'
                });
            } else if (res.downloadUrl) {
                // Large files come back as a URL. This fetch is a plain one against
                // Graph, so incremental progress survives where it matters most.
                const downloadResp = await fetch(res.downloadUrl);
                if (!downloadResp.ok) throw new Error('Failed to download document from URL');
                const contentLength = Number.parseInt(downloadResp.headers.get('Content-Length') || '0', 10);
                const reader = downloadResp.body?.getReader();
                let blob;
                if (reader && contentLength > 0) {
                    let loaded = 0;
                    const chunks = [];
                    while(true){
                        const { done, value } = await reader.read();
                        if (done) break;
                        chunks.push(value);
                        loaded += value.length;
                        const pct = Math.round(loaded / contentLength * 100);
                        uploads.updateRow(rowId, (r)=>({
                                ...r,
                                progress: pct
                            }));
                    }
                    blob = new Blob(chunks);
                } else {
                    blob = await downloadResp.blob();
                }
                sharePointFile = new File([
                    blob
                ], res.fileName || document.title, {
                    type: res.mimeType || downloadResp.headers.get('Content-Type') || 'application/octet-stream'
                });
            } else {
                throw new Error('SharePoint returned no content for this document');
            }
            if (sharePointFile.size === 0) throw new Error('SharePoint returned an empty document');
            sharePointDownloads.current.delete(rowId);
            // Download complete — set to 'pending' so normal upload+processing flow handles it on submit
            uploads.updateRow(rowId, (r)=>({
                    ...r,
                    file: sharePointFile,
                    progress: 0,
                    status: 'pending',
                    sourceDownloadPending: false,
                    statusLabel: undefined,
                    contentType: sharePointFile.type
                }));
        } catch (err) {
            // Update the placeholder file to error state
            uploads.updateRow(rowId, (r)=>({
                    ...r,
                    status: 'error',
                    statusLabel: apiErrorMessage(err, 'Download failed')
                }));
        }
    };
    const handleSubmit = async ()=>{
        if (uploads.hasFailedAttachments || uploads.rows.some((row)=>row.sourceDownloadPending)) return;
        if (!prompt.trim() && uploads.rows.length === 0) {
            return;
        }
        isSubmittingRef.current = true; // Set ref immediately (synchronous)
        try {
            setIsSubmitting(true); // Also set state for consistency
            // Generate markdown using tiptap-markdown if registered; otherwise fallback to text
            let markdown = editor?.getText() ?? '';
            try {
                const storage = editor?.storage ?? {};
                const getMd = storage.markdown?.getMarkdown;
                if (typeof getMd === 'function') {
                    markdown = getMd();
                }
            } catch  {
            // ignore and use plain text
            }
            // If there are files, upload (and wait on processing) via the hook.
            // uploads.submit() only resolves once every row has reached a terminal
            // state AND that state has been flushed to a committed render — so the
            // rows are observably ready before we ever reach uploads.clear() below.
            let uploadedAttachments = [];
            if (uploads.rows.length > 0) {
                if (!user || !user.tenant_id) {
                    toast.error('You must be logged in (with a tenant) to upload files');
                    setIsSubmitting(false);
                    isSubmittingRef.current = false;
                    return;
                }
                const result = await uploads.submit({
                    tenantId: user.tenant_id,
                    ownerUserId: user.id,
                    chatId: attachmentChatId?.replace(/^shanks-/, '') || undefined
                }, {
                    onPollStart: ()=>setIsProcessingDocs(true)
                });
                setIsProcessingDocs(false);
                if (!result.ok) {
                    setIsSubmitting(false);
                    isSubmittingRef.current = false;
                    return;
                }
                uploadedAttachments = result.attachments;
                // SAD-741: onSubmit (e.g. PromptComposer's new-chat handler) can
                // resolve and navigate away synchronously once called — with no
                // network wait of its own — so the flushSync-committed "all ready"
                // render above can otherwise be overwritten before anything (a
                // user's eye, a test) ever gets a macrotask-boundary chance to
                // observe it. A zero-delay timeout yields past that boundary.
                await new Promise((resolve)=>setTimeout(resolve, 0));
            }
            // Make sure we have either a prompt or attachments
            if (!markdown.trim() && uploadedAttachments.length === 0) {
                alert('Please enter a message or upload a file');
                setIsProcessingDocs(false);
                return;
            }
            // Call parent's onSubmit callback (remove jobId from attachments since it's internal)
            try {
                const payload = {
                    prompt: quickToggle ? applyQuickPrefix(markdown, quick) : markdown,
                    files: [],
                    attachments: uploadedAttachments.map(({ jobId, ...att })=>att)
                };
                if (!deferClear) {
                    // Clear chips as soon as attachment ids are handed off — AssistRail /
                    // ChatView await the full stream, so waiting for onSubmit would leave
                    // chips stuck for the whole turn (SAD-899 live check).
                    setPrompt('');
                    uploads.clear();
                    editor?.commands.clearContent(true);
                }
                await onSubmit(payload);
            } catch (submitError) {
                setIsProcessingDocs(false); // Reset on error
                // Show error to user - you might want to add a toast notification here
                alert(`Failed to submit: ${submitError instanceof Error ? submitError.message : 'Unknown error'}`);
                throw submitError; // Re-throw to be handled by outer catch
            }
        } catch (error) {
            setIsProcessingDocs(false); // Reset on any error
            if (error instanceof Error) {
                alert(`Error: ${error.message}`);
            }
        } finally{
            // Always reset the ref when handleSubmit completes
            isSubmittingRef.current = false;
            if (submitting === undefined) {
                setIsSubmitting(false);
            }
            setIsProcessingDocs(false); // Always reset this
        }
    };
    // If deferring clearing, watch resetKey and clear when it changes
    useEffect(()=>{
        if (!deferClear) return;
        if (resetKey === undefined) return;
        setPrompt('');
        uploads.clear();
        if (submitting === undefined) {
            setIsSubmitting(false);
            isSubmittingRef.current = false; // Reset ref
        }
        editor?.commands.clearContent(true);
    }, [
        resetKey,
        deferClear,
        submitting,
        editor,
        uploads.clear
    ]);
    // Update editor's editable state when isBusy changes
    useEffect(()=>{
        if (editor) {
            editor.setEditable(!isBusy);
        }
    }, [
        editor,
        isBusy
    ]);
    // Aggregate attachment status so the banner + submit-gate share one source of
    // truth. "Failed" covers both upload errors (status==='error') and processing
    // errors (processingStatus==='error') — either is reason enough to block
    // submission. "Pending" covers anything still in flight (uploading or
    // processing) so the banner can show a useful "n of m" while we wait.
    const { failedFileCount, pendingFileCount, hasFailedAttachments, hasPendingAttachments } = uploads;
    const hasDownloadingAttachments = uploads.rows.some((row)=>row.sourceDownloadPending);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs("section", {
                "aria-label": "Prompt input dropzone",
                ref: editorRootRef,
                className: cn(// Framed composer: a single hairline box holding the input and its
                // icon row (no inner card shadow), per the delivery-redesign spec.
                // `dense` (the Assist rail) keeps its own outer frame, so no box here.
                'relative outline-none focus:outline-none', dense ? 'bg-transparent p-0' : 'rounded-lg border border-border-strong bg-background'),
                onDragEnter: handleDragEnter,
                onDragLeave: handleDragLeave,
                onDragOver: handleDragOver,
                onDrop: handleDrop,
                onCompositionStart: ()=>setIsComposing(true),
                onCompositionEnd: ()=>setIsComposing(false),
                children: [
                    isDragging && /*#__PURE__*/ _jsx("div", {
                        className: "absolute inset-0 bg-primary/10 border-2 border-dashed border-primary rounded-lg flex items-center justify-center z-10",
                        children: /*#__PURE__*/ _jsxs("div", {
                            className: "text-center",
                            children: [
                                /*#__PURE__*/ _jsx(UploadCloud, {
                                    className: "w-10 h-10 text-primary mx-auto"
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    className: "mt-2 font-semibold text-primary",
                                    children: "Drop files to upload"
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: cn('relative', isBusy && 'opacity-50 pointer-events-none', !dense && 'px-3.5 pt-3 pb-1'),
                        children: [
                            mention !== null && mode === 'chat' ? /*#__PURE__*/ _jsx(MentionMenu, {
                                active: mentionIndex,
                                hits: mentionResults.hits,
                                loading: mentionResults.loading,
                                onPick: pickMention,
                                query: mention
                            }) : null,
                            /*#__PURE__*/ _jsx(EditorContent, {
                                editor: editor
                            })
                        ]
                    }),
                    uploads.rows.length > 0 && (hasPendingAttachments || hasFailedAttachments) && /*#__PURE__*/ _jsx("output", {
                        "data-testid": Testids.assistant.chatAttachmentBanner,
                        "data-attachment-banner-state": hasFailedAttachments ? 'error' : 'pending',
                        className: cn('mt-3 mb-1 rounded-md px-3 py-2 text-xs flex items-center justify-between gap-3', !dense && 'mx-3.5', hasFailedAttachments ? 'bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-200' : 'bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-200'),
                        "aria-live": "polite",
                        children: /*#__PURE__*/ _jsx("span", {
                            children: hasFailedAttachments ? `${failedFileCount} of ${uploads.rows.length} attachment${uploads.rows.length === 1 ? '' : 's'} failed. Retry or remove to send.` : `${uploads.rows.length - pendingFileCount} of ${uploads.rows.length} attachment${uploads.rows.length === 1 ? '' : 's'} ready. Processing the rest…`
                        })
                    }),
                    uploads.rows.length >= COMPACT_ATTACHMENT_THRESHOLD && /*#__PURE__*/ _jsx("div", {
                        className: cn('my-4', !dense && 'px-3.5'),
                        children: /*#__PURE__*/ _jsx(CompactAttachmentList, {
                            rows: uploads.rows,
                            disabled: isBusy,
                            onRemove: (id)=>uploads.removeRow(id),
                            onRemoveAll: ()=>{
                                for (const row of uploads.rows)uploads.removeRow(row.id);
                            },
                            onRetry: (id)=>void handleRetry(id),
                            onJobTerminal: (id, terminal)=>{
                                uploads.updateRow(id, (f)=>({
                                        ...f,
                                        processingStatus: terminal === 'completed' ? 'ready' : 'error',
                                        statusLabel: terminal === 'cancelled' ? 'Processing cancelled' : f.statusLabel
                                    }));
                            }
                        })
                    }),
                    uploads.rows.length > 0 && uploads.rows.length < COMPACT_ATTACHMENT_THRESHOLD && /*#__PURE__*/ _jsx("div", {
                        className: cn('space-y-3 my-4', !dense && 'px-3.5'),
                        children: uploads.rows.map((pf)=>/*#__PURE__*/ _jsx(FileAttachment, {
                                file: pf.file,
                                progress: pf.progress,
                                status: pf.status,
                                statusLabel: pf.statusLabel,
                                rejection: pf.rejection,
                                sourceArchiveName: pf.sourceArchiveName,
                                jobId: pf.jobId,
                                processingStatus: pf.processingStatus,
                                onRemove: ()=>uploads.removeRow(pf.id),
                                onRetry: ()=>void handleRetry(pf.id),
                                onJobTerminal: (_jobId, terminal)=>{
                                    // Addressed by this row's own stable id — never by index.
                                    uploads.updateRow(pf.id, (f)=>({
                                            ...f,
                                            processingStatus: terminal === 'completed' ? 'ready' : 'error',
                                            statusLabel: terminal === 'cancelled' ? 'Processing cancelled' : f.statusLabel
                                        }));
                                },
                                disabled: isBusy,
                                useDeepProcessing: pf.useDeepProcessing,
                                onToggleDeepProcessing: ()=>{
                                    uploads.updateRow(pf.id, (f)=>({
                                            ...f,
                                            useDeepProcessing: !f.useDeepProcessing
                                        }));
                                },
                                outputFormat: pf.outputFormat,
                                onOutputFormatChange: (format)=>{
                                    uploads.updateRow(pf.id, (f)=>({
                                            ...f,
                                            outputFormat: format
                                        }));
                                }
                            }, pf.id))
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: cn('flex items-center justify-between gap-2 min-w-0 flex-wrap', dense ? 'mt-1' : 'gap-1.5 px-2.5 pt-1.5 pb-2.5'),
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: cn('flex items-center min-w-0 flex-wrap', dense ? 'gap-2' : 'gap-1.5'),
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraIconButton, {
                                        label: "Upload files",
                                        title: "Upload files",
                                        size: "sm",
                                        variant: "ghost",
                                        disabled: isBusy,
                                        "data-testid": Testids.assistant.composerUploadButton,
                                        onClick: openFileDialog,
                                        children: /*#__PURE__*/ _jsx(Paperclip, {
                                            "aria-hidden": "true"
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraIconButton, {
                                        label: "Add from Documents",
                                        title: "Add from Documents",
                                        size: "sm",
                                        variant: "ghost",
                                        disabled: isBusy,
                                        "data-testid": Testids.assistant.addFromDocumentsButton,
                                        onClick: ()=>setIsDocumentLibraryOpen(true),
                                        children: /*#__PURE__*/ _jsx(Library, {
                                            "aria-hidden": "true"
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraIconButton, {
                                        label: "Add from SharePoint",
                                        title: "Add from SharePoint",
                                        size: "sm",
                                        variant: "ghost",
                                        disabled: isBusy,
                                        "data-testid": Testids.assistant.composerDocumentPickerButton,
                                        onClick: ()=>setIsSharePointPickerOpen(true),
                                        children: /*#__PURE__*/ _jsx(Share2, {
                                            "aria-hidden": "true"
                                        })
                                    }),
                                    mode === 'chat' && onRemember ? /*#__PURE__*/ _jsx(SpecraIconButton, {
                                        "data-testid": Testids.memory.rememberButton,
                                        disabled: rememberDisabled || isBusy,
                                        label: "Remember this",
                                        onClick: ()=>onRemember(editor?.getText() ?? ''),
                                        size: "sm",
                                        variant: "ghost",
                                        children: /*#__PURE__*/ _jsx(Brain, {
                                            "aria-hidden": "true"
                                        })
                                    }) : null,
                                    /*#__PURE__*/ _jsx(VoiceInputButton, {
                                        dense: dense,
                                        onTranscript: insertDictation,
                                        disabled: isBusy
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: cn('flex items-center', dense ? 'gap-2' : 'gap-2.5'),
                                children: [
                                    !dense && !isBusy && /*#__PURE__*/ _jsx("span", {
                                        className: "hidden text-[11px] text-muted-foreground sm:inline",
                                        children: "↵ to send"
                                    }),
                                    quickToggle ? /*#__PURE__*/ _jsx(SpecraButton, {
                                        size: "sm",
                                        variant: quick ? 'secondary' : 'ghost',
                                        "aria-pressed": quick,
                                        title: quick ? 'Quick is on: fast answers from a few reads, no questions' : 'Answer fast from a few reads, no questions',
                                        "data-testid": Testids.assistant.quickToggle,
                                        onClick: toggleQuick,
                                        className: cn('flex-shrink-0', quick ? 'text-primary' : 'text-muted-foreground'),
                                        iconLeft: /*#__PURE__*/ _jsx(Zap, {
                                            className: cn(quick && 'fill-current')
                                        }),
                                        children: "Quick"
                                    }) : null,
                                    isBusy && onStop ? dense ? /*#__PURE__*/ _jsx(Button, {
                                        onClick: onStop,
                                        size: "icon",
                                        className: "h-[26px] w-[26px] flex-shrink-0 rounded-md bg-primary text-primary-foreground hover:bg-primary/90",
                                        title: "Stop",
                                        "aria-label": "Stop",
                                        "data-testid": Testids.assistant.stopButton,
                                        children: /*#__PURE__*/ _jsx(Square, {
                                            className: "h-3 w-3 fill-current"
                                        })
                                    }) : /*#__PURE__*/ _jsx(SpecraButton, {
                                        className: "flex-shrink-0",
                                        "data-testid": Testids.assistant.stopButton,
                                        iconLeft: /*#__PURE__*/ _jsx(Square, {
                                            "aria-hidden": "true",
                                            className: "fill-current"
                                        }),
                                        onClick: onStop,
                                        size: "sm",
                                        children: "Stop"
                                    }) : dense ? /*#__PURE__*/ _jsx(Button, {
                                        onClick: handleSubmit,
                                        size: "icon",
                                        className: "h-[26px] w-[26px] flex-shrink-0 rounded-md bg-primary text-primary-foreground hover:bg-primary/90",
                                        disabled: isBusy || hasFailedAttachments || hasDownloadingAttachments,
                                        title: hasFailedAttachments ? 'Fix failed attachments' : submitLabel,
                                        "aria-label": submitLabel,
                                        "data-testid": Testids.assistant.sendMessageButton,
                                        children: /*#__PURE__*/ _jsx(ArrowUp, {
                                            className: "h-[13px] w-[13px]"
                                        })
                                    }) : /*#__PURE__*/ _jsx(SpecraButton, {
                                        className: "flex-shrink-0",
                                        "data-testid": Testids.assistant.sendMessageButton,
                                        disabled: isBusy || hasFailedAttachments || hasDownloadingAttachments,
                                        onClick: handleSubmit,
                                        size: "sm",
                                        children: isProcessingDocs ? 'Processing documents…' : isBusy ? 'Sending…' : hasFailedAttachments ? 'Fix failed attachments' : submitLabel
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("input", {
                        type: "file",
                        ref: fileInputRef,
                        onChange: handleFileChange,
                        className: "hidden",
                        multiple: true
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(DocumentLibraryPicker, {
                open: isDocumentLibraryOpen,
                onOpenChange: setIsDocumentLibraryOpen,
                onSelect: handleLibraryDocumentSelect
            }),
            /*#__PURE__*/ _jsx(SharePointDocumentPicker, {
                isOpen: isSharePointPickerOpen,
                onClose: ()=>setIsSharePointPickerOpen(false),
                onSelect: handleSharePointDocumentSelect
            })
        ]
    });
}
